import {useCallback, useEffect, useMemo, useState} from 'react'
import {Link} from 'react-router-dom'
import {useEscopo} from '../ServiceLevelContext'
import {hasServiceLevelPermission} from '../permissions'
import {Check, Search, Timer} from 'lucide-react'
import {getSlaMatrix, salvarSlaSeller, SlaMatrix} from '../api'
import Dica from '../components/Dica'
import {Seller} from '../types'

const AJUDA_SLA =
  'Prazo em dias para o seller enviar o pedido após o recebimento. É a referência dos pedidos em atraso (P1) e do indicador % no prazo. Aplicar cria o mesmo prazo em dias corridos para todas as regiões do seller. Para diferenças por região ou dias úteis, use Regiões e SLAs.'

export default function AdminSlaSellers() {
  const podeOutraTela = hasServiceLevelPermission(useEscopo(), 'regioes_slas')
  const [data, setData] = useState<SlaMatrix | null>(null)
  const [busca, setBusca] = useState('')
  const [dias, setDias] = useState<Record<number, string>>({})
  const [salvando, setSalvando] = useState<number | null>(null)
  const [salvos, setSalvos] = useState<Set<number>>(new Set())
  const [erro, setErro] = useState('')
  const [errosSeller, setErrosSeller] = useState<Record<number, string>>({})
  const load = useCallback(async () => {
    const next = await getSlaMatrix()
    setData(next)
    setErro('')
  }, [])
  useEffect(() => {
    load().catch((error) =>
      setErro(error instanceof Error ? error.message : 'Não foi possível carregar os SLAs.')
    )
  }, [load])
  const situacao = useMemo(() => {
    const map = new Map<
      number,
      {texto: string; uniformeDias: number | null; configuradas: number; status: string}
    >()
    if (!data) return map
    for (const seller of data.sellers) {
      const slas = data.slas.filter((sla) => sla.seller_id === seller.seller_id)
      const prazos = Array.from(
        new Set(
          slas.map(
            (sla) =>
              `${sla.prazo_horas / 24} dias ${sla.tipo_contagem === 'UTEIS' ? 'úteis' : 'corridos'}`
          )
        )
      )
      map.set(
        seller.seller_id,
        prazos.length === 0
          ? {
              texto: `${data.default_horas / 24} dias corridos (padrão)`,
              uniformeDias: null,
              configuradas: 0,
              status: 'default-sla',
            }
          : prazos.length === 1
          ? {
              texto: prazos[0],
              uniformeDias: slas[0].prazo_horas / 24,
              configuradas: slas.length,
              status: 'FINALIZADO',
            }
          : {
              texto: 'Prazos diferentes por região',
              uniformeDias: null,
              configuradas: slas.length,
              status: 'p1',
            }
      )
    }
    return map
  }, [data])
  const valid = (sellerId: number) => {
    const value = Number(dias[sellerId])
    return Number.isInteger(value) && value >= 1
  }
  const salvar = async (sellerId: number) => {
    if (salvando !== null || !valid(sellerId)) return
    setSalvando(sellerId)
    setErrosSeller((previous) => ({...previous, [sellerId]: ''}))
    try {
      await salvarSlaSeller(sellerId, Number(dias[sellerId]))
      setDias((previous) => ({...previous, [sellerId]: ''}))
      setSalvos((previous) => new Set(previous).add(sellerId))
      try {
        await load()
      } catch {
        setErro('O prazo foi salvo. Não foi possível atualizar a lista; tente carregar novamente.')
      }
    } catch (error) {
      setErrosSeller((previous) => ({
        ...previous,
        [sellerId]:
          error instanceof Error
            ? error.message
            : 'Não foi possível salvar o prazo. Tente novamente.',
      }))
    } finally {
      setSalvando(null)
    }
  }
  const editor = (seller: Seller, context: string) => {
    const value = dias[seller.seller_id] ?? ''
    const invalid = value !== '' && !valid(seller.seller_id)
    const error =
      errosSeller[seller.seller_id] ||
      (invalid ? 'Informe um número inteiro de dias maior que zero.' : '')
    const inputId = `sla-${context}-${seller.seller_id}`
    return (
      <div className='admin-sla-editor'>
        <label htmlFor={inputId}>Novo prazo (dias corridos)</label>
        <div className='admin-sla-controls'>
          <input
            id={inputId}
            type='number'
            inputMode='numeric'
            min='1'
            step='1'
            placeholder={situacao.get(seller.seller_id)?.uniformeDias?.toString() || 'Ex.: 3'}
            value={value}
            disabled={salvando !== null}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
            onChange={(event) => {
              setDias((previous) => ({...previous, [seller.seller_id]: event.target.value}))
              setSalvos((previous) => {
                const next = new Set(previous)
                next.delete(seller.seller_id)
                return next
              })
              setErrosSeller((previous) => ({...previous, [seller.seller_id]: ''}))
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                salvar(seller.seller_id)
              }
            }}
          />
          <button
            type='button'
            className='btn'
            data-analytics-feature='Nível de Serviço · Aplicar SLA por Seller'
            onClick={() => salvar(seller.seller_id)}
            disabled={salvando !== null || !valid(seller.seller_id) || !data?.regioes.length}
          >
            {salvando === seller.seller_id ? 'Salvando…' : 'Aplicar'}
          </button>
        </div>
        {error && (
          <p id={`${inputId}-error`} className='field-error' role='alert'>
            {error}
          </p>
        )}
        {salvos.has(seller.seller_id) && (
          <span
            className='delta-good inline-flex items-center gap-1 text-xs font-semibold'
            role='status'
          >
            <Check size={15} /> Prazo salvo em todas as regiões
          </span>
        )}
      </div>
    )
  }
  if (!data)
    return (
      <div className='card'>
        {erro ? (
          <div className='feedback error' role='alert'>
            {erro}
            <button
              type='button'
              className='btn secondary'
              onClick={() =>
                load().catch((error) =>
                  setErro(error instanceof Error ? error.message : 'Falha ao carregar.')
                )
              }
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <p className='empty' role='status'>
            Carregando SLAs…
          </p>
        )}
      </div>
    )
  const query = busca.trim().toLocaleLowerCase('pt-BR')
  const lista = data.sellers.filter(
    (seller) =>
      !query ||
      `${seller.nome_fantasia} ${seller.razao_social} ${seller.uf_base}`
        .toLocaleLowerCase('pt-BR')
        .includes(query)
  )
  const configurados = data.sellers.filter(
    (seller) => (situacao.get(seller.seller_id)?.configuradas ?? 0) > 0
  ).length

  return (
    <>
      <div className='page-header'>
        <div>
          <h1 className='flex items-center gap-2'>
            <Timer size={24} /> SLA por seller <Dica titulo='SLA de envio' texto={AJUDA_SLA} />
          </h1>
          <div className='sub'>
            Um prazo de envio para todas as regiões.{' '}
            {podeOutraTela && (
              <Link to='/service-level/admin/regioes-slas'>Ajustar por região →</Link>
            )}
          </div>
        </div>
        <div className='filters'>
          <label className='filter-field' htmlFor='admin-seller-search'>
            <span>Buscar seller</span>
            <input
              id='admin-seller-search'
              type='search'
              placeholder='Nome ou UF…'
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
            />
          </label>
        </div>
      </div>
      <div className='admin-summary'>
        <div>
          <strong>{data.sellers.length}</strong>
          <span>Sellers cadastrados</span>
        </div>
        <div>
          <strong>{configurados}</strong>
          <span>Com SLA configurado</span>
        </div>
        <div>
          <strong>{data.default_horas / 24} dias</strong>
          <span>Prazo padrão global</span>
        </div>
      </div>
      {erro && (
        <div className='feedback error' role='alert'>
          {erro}
          <button
            type='button'
            className='btn secondary'
            onClick={() => load().catch(() => setErro('Não foi possível atualizar os SLAs.'))}
          >
            Carregar novamente
          </button>
        </div>
      )}
      <section className='card'>
        <h2>
          {lista.length} {lista.length === 1 ? 'seller encontrado' : 'sellers encontrados'}
        </h2>
        <div className='table-wrap desktop-only'>
          <table>
            <thead>
              <tr>
                <th>Seller</th>
                <th>UF</th>
                <th>SLA atual</th>
                <th>Regiões configuradas</th>
                <th>Atualizar prazo</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((seller) => {
                const sit = situacao.get(seller.seller_id)!
                return (
                  <tr key={seller.seller_id}>
                    <td>
                      <strong>{seller.nome_fantasia}</strong>
                    </td>
                    <td>{seller.uf_base}</td>
                    <td>
                      <span className={`badge ${sit.status}`}>{sit.texto}</span>
                    </td>
                    <td>
                      {sit.configuradas}/{data.regioes.length}
                    </td>
                    <td>{editor(seller, 'desktop')}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className='mobile-only'>
          {lista.map((seller) => {
            const sit = situacao.get(seller.seller_id)!
            return (
              <article
                key={seller.seller_id}
                className='admin-seller-card'
                aria-label={`SLAs de ${seller.nome_fantasia}`}
              >
                <div className='admin-seller-head'>
                  <strong>{seller.nome_fantasia}</strong>
                  <span className='badge CAPTADO'>{seller.uf_base}</span>
                </div>
                <div className='admin-seller-meta'>
                  <span className={`badge ${sit.status}`}>{sit.texto}</span>
                  <span>
                    {sit.configuradas} de {data.regioes.length} regiões configuradas
                  </span>
                </div>
                {editor(seller, 'mobile')}
              </article>
            )
          })}
        </div>
        {lista.length === 0 && (
          <p className='empty'>
            <Search size={24} className='mx-auto mb-2' />
            Nenhum seller encontrado. Tente outro nome ou UF.
          </p>
        )}
        <p className='chart-note'>
          Aplicar substitui os prazos de todas as regiões por dias corridos. Cada alteração cria uma
          nova vigência; os pedidos passados mantêm o SLA válido na época.
        </p>
      </section>
    </>
  )
}
